import { useContext, useState } from 'react';

import { Button, Flex, HStack, Stack, Text, useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  IconDelete,
  IconDownload,
  IconPlayVideo,
  IconVideoPurple,
} from '@/components/Icons/icons-generated/';
import { UploadIntroductionVideo } from '@/features/admin-interview';
import {
  adminInterviewKeys,
  useDeleteInterviewInstructionVideoMutation,
  useGetInterviewQuery,
} from '@/features/admin-interview/useAdminInterviewQueries';

import { RecordIntroductionVideo } from './IntroductionVideoRecorder';
import { AdminNewInterviewContext } from './PageAdminNewInterview.ctx';
import { formatRecordingSize } from './common';

export const RecordIntroductionVideoSection: React.FC = () => {
  const { t } = useTranslation(['interview']);
  const [isOpen, setIsOpen] = useState(false);
  const { interviewId } = useContext(AdminNewInterviewContext);
  const [localVideo, setLocalVideo] = useState<File | null>(null);
  const toast = useToast();

  const queryClient = useQueryClient();
  const deleteInterviewInstructionVideoMutation =
    useDeleteInterviewInstructionVideoMutation({
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: adminInterviewKeys.getInterview({
            interviewsId: interviewId.current!,
          }),
        });
        setLocalVideo(null);
        toast({
          title: t('interview:introductionVideo.deleteSuccessFull'),
          status: 'success',
        });
      },
    });

  const getInterviewQuery = useGetInterviewQuery(
    {
      interviewsId: interviewId.current!,
    },
    {
      enabled: !!interviewId.current,
    }
  );

  const handleFileDelete = () => {
    if (
      !interviewId.current ||
      !getInterviewQuery.data?.interview?.instructionVideoUrl
    )
      return;

    deleteInterviewInstructionVideoMutation.mutateAsync({
      id: interviewId.current,
    });
  };

  return (
    <>
      <Stack
        borderColor="primary.50"
        borderRadius="lg"
        borderWidth="1px"
        w="full"
        p={4}
      >
        <HStack w="full" justifyContent="space-between">
          <HStack>
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              h={12}
              w={12}
              bg="primary.50"
            >
              <IconPlayVideo fontSize={24} />
            </Flex>
            <Stack spacing={0}>
              <Text color="primary.500">
                {t('interview:introductionVideo.createIntroductionVideo')}
              </Text>
              <Text fontSize={14} color="gray.600">
                {t('interview:introductionVideo.createIntroductionVideoNow')}
              </Text>
            </Stack>
          </HStack>
          {!getInterviewQuery.data?.interview?.instructionVideoUrl && (
            <HStack alignItems="center">
              <Button
                onClick={() => setIsOpen(true)}
                px={3}
                py={2}
                columnGap={2}
                h={8}
                borderRadius="8"
              >
                <IconVideoPurple fontSize={20} />
                <Text fontSize={14} color="primary.500">
                  {t('interview:introductionVideo.createRecord')}
                </Text>
              </Button>
              {interviewId.current && (
                <UploadIntroductionVideo
                  setVideo={setLocalVideo}
                  interviewId={interviewId.current}
                />
              )}
            </HStack>
          )}
        </HStack>
        {(localVideo ??
          getInterviewQuery.data?.interview?.instructionVideoUrl) && (
          <HStack
            w={360}
            ml={{ md: 12 }}
            px={3}
            py={1}
            borderRadius="xl"
            bg="gray.25"
            borderWidth="1px"
            borderColor="gray.50"
            justifyContent="space-between"
          >
            <HStack alignItems="center">
              <IconDownload fontSize={22} />
              <Text fontSize={14} fontWeight={500} noOfLines={1}>
                {localVideo
                  ? localVideo.name
                  : t('interview:introductionVideo.introductionVideo')}
              </Text>
              {localVideo && (
                <>
                  <Text color="gray.200" fontSize={14}>
                    •
                  </Text>
                  <Text color="gray.600" fontSize={14} whiteSpace="nowrap">
                    {localVideo && formatRecordingSize(localVideo.size)}
                  </Text>
                </>
              )}
            </HStack>
            <Button
              display="flex"
              justifyContent="center"
              alignItems="center"
              variant="unstyled"
              outline="none"
              border="none"
              shadow="none"
              p={0}
              isLoading={deleteInterviewInstructionVideoMutation.isPending}
              onClick={handleFileDelete}
            >
              <IconDelete color="gray.500" fontSize={22} />
            </Button>
          </HStack>
        )}
      </Stack>
      {interviewId.current && isOpen && (
        <RecordIntroductionVideo
          interviewId={interviewId.current}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
